import React, { useCallback, useContext } from 'react';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { SECTION_ITEM_MORE } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import {
	NavItemsContext,
	PeekMenuIdContext,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import type { JiraConfigurableNavigationItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useSectionItemAnalytics } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import type { ConfigurableMenuItem$key } from '@atlassian/jira-relay/src/__generated__/ConfigurableMenuItem.graphql';
import { GlobalMoreMenu } from '@atlassian/global-more-menu';
import { useIntl } from '@atlassian/jira-intl';
import type { CustomizableMenuItemData } from '@atlassian/customization-modal';
import { MoreMenuItem } from './more-menu-item/index.tsx';
import { PeekMenuItem } from './peek-menu-item/index.tsx';
import messages from './messages.tsx';
import { CustomizeSidebar } from './customize-sidebar/index.tsx';

export type MoreMenuItemProps = {
	navItems: JiraConfigurableNavigationItem[];
	menuData: ConfigurableMenuItem$key;
	productItemsData: CustomizableMenuItemData[];
	appItemsData: CustomizableMenuItemData[];
	getAppShortcutURL?: (apsKey: string) => string | undefined;
};

export function SidebarCustomization({
	menuData,
	productItemsData,
	appItemsData,
	getAppShortcutURL,
}: MoreMenuItemProps): React.JSX.Element {
	const { moreMenuIds } = useContext(NavItemsContext);
	const { peekMenuId } = useContext(PeekMenuIdContext);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();
	const pressTracing = usePressTracing('nav4-sidebar-more');

	const { formatMessage } = useIntl();

	const onClickMoreMenuTrigger = useCallback(() => {
		sendSectionItemAnalyticsEvent({ sectionItem: SECTION_ITEM_MORE });
		pressTracing();
	}, [pressTracing, sendSectionItemAnalyticsEvent]);

	return moreMenuIds.length === 0 ? (
		<CustomizeSidebar productItemsData={productItemsData} appItemsData={appItemsData} />
	) : (
		<>
			<PeekMenuItem menuData={menuData} />
			<GlobalMoreMenu
				productItemsData={productItemsData}
				appItemsData={appItemsData}
				menuItemComponent={(props) => (
					<MoreMenuItem {...props} getAppShortcutURL={getAppShortcutURL} />
				)}
				customizeSidebarMenuItem={
					<CustomizeSidebar productItemsData={productItemsData} appItemsData={appItemsData} />
				}
				moreMenuItemLabel={formatMessage(messages.more)}
				peekingMenuIds={peekMenuId ? [peekMenuId] : undefined}
				onClickMoreMenuTrigger={onClickMoreMenuTrigger}
			/>
		</>
	);
}
